import { matchReasons, isStrong } from './dedupe';
﻿// Contact import: header synonyms + data-pattern detection with a confidence score per column.
export const IMPORT_FIELDS = [
  ['firstName', 'First name'], ['lastName', 'Last name'], ['fullName', 'Full name (split)'], ['phone', 'Phone'], ['email', 'Email'],
  ['street', 'Street'], ['city', 'City'], ['state', 'State'], ['zip', 'ZIP'], ['contactTypes', 'Contact type'], ['source', 'Source'],
  ['tags', 'Tags'], ['notes', 'Notes'], ['consentText', 'Text consent (Y/N)'], ['consentEmail', 'Email consent (Y/N)'], ['assignedEmail', 'Assigned agent email'], ['dnc', 'Do Not Call (Y/N)'],
];
export const TEMPLATE_HEADERS = ['First name', 'Last name', 'Phone', 'Email', 'Street', 'City', 'State', 'ZIP', 'Contact type', 'Source', 'Tags', 'Notes', 'Text consent (Y/N)', 'Email consent (Y/N)', 'Assigned agent email'];
export const TEMPLATE_EXAMPLES = [
  ['John', 'Smith', '239-555-0101', 'john.smith@example.com', '123 Palm Cove Dr', 'Fort Myers', 'FL', '33908', 'Seller, Buyer', 'Community Farming', 'Gateway', 'Thinking of selling in spring', 'Y', 'Y', ''],
  ['Maria', 'Lopez', '(239) 555-0102', 'maria.lopez@example.com', '', 'Cape Coral', 'FL', '33904', 'Buyer', 'Open House', 'first-time buyer', '', 'N', 'Y', ''],
];

const norm = (h) => String(h || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
// Header synonyms (Sisu, Follow Up Boss, kvCORE, Google Contacts, Outlook exports and common variants).
const SYN = {
  firstName: ['first name', 'first', 'firstname', 'given name', 'fname', 'contact first name', 'primary first name', 'buyer first name', 'client first name'],
  lastName: ['last name', 'last', 'lastname', 'surname', 'family name', 'lname', 'contact last name', 'primary last name', 'client last name'],
  fullName: ['name', 'full name', 'contact name', 'display name', 'client name', 'contact', 'lead name'],
  phone: ['phone', 'phone number', 'mobile', 'mobile phone', 'cell', 'cell phone', 'phone 1', 'phone 1 value', 'phone1', 'primary phone', 'home phone', 'business phone', 'work phone', 'telephone', 'tel', 'phone mobile', 'phone (mobile)', 'phone 1 - value', 'contact phone', 'cellphone'],
  email: ['email', 'e mail', 'email address', 'e mail address', 'email 1', 'email1', 'email 1 value', 'primary email', 'email 1 - value', 'contact email', 'personal email', 'e-mail'],
  street: ['street', 'address', 'address 1', 'address1', 'street address', 'mailing address', 'home street', 'address line 1', 'property address', 'address 1 street', 'business street', 'home address', 'mailing street'],
  city: ['city', 'town', 'home city', 'address 1 city', 'mailing city', 'business city', 'property city'],
  state: ['state', 'st', 'province', 'region', 'home state', 'address 1 region', 'mailing state', 'business state', 'state province'],
  zip: ['zip', 'zip code', 'zipcode', 'postal', 'postal code', 'postcode', 'home postal code', 'address 1 postal code', 'mailing zip', 'business postal code'],
  contactTypes: ['contact type', 'type', 'contact types', 'lead type', 'client type', 'category', 'categories'],
  source: ['source', 'lead source', 'how heard', 'origin', 'referral source'],
  tags: ['tags', 'tag', 'labels', 'groups', 'group membership', 'lists'],
  notes: ['notes', 'note', 'comments', 'description', 'background', 'memo'],
  consentText: ['text consent', 'text consent y n', 'sms consent', 'sms opt in', 'text opt in', 'ok to text'],
  consentEmail: ['email consent', 'email consent y n', 'email opt in', 'ok to email', 'marketing email consent'],
  dnc: ['dnc', 'do not call', 'do not call y n', 'on dnc', 'dnc list'],
  assignedEmail: ['assigned agent email', 'agent email', 'assigned to', 'assigned agent', 'owner email', 'assigned user', 'agent'],
};
const EMAIL = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i;
const STATES = new Set('AL AK AZ AR CA CO CT DE FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY DC PR'.split(' '));
export const digits10 = (v) => { let d = String(v || '').replace(/\D/g, ''); if (d.length === 11 && d[0] === '1') d = d.slice(1); return d.length === 10 ? d : ''; };

const patternGuess = (vals) => {
  const v = vals.map((x) => String(x || '').trim()).filter(Boolean); if (!v.length) return null;
  const share = (fn) => v.filter(fn).length / v.length;
  if (share((x) => EMAIL.test(x)) >= 0.8) return ['email', 0.85];
  if (share((x) => !!digits10(x)) >= 0.8) return ['phone', 0.8];
  if (share((x) => /^\d{5}(-\d{4})?$/.test(x)) >= 0.8) return ['zip', 0.75];
  if (share((x) => STATES.has(x.toUpperCase())) >= 0.8) return ['state', 0.75];
  if (share((x) => /^(y|n|yes|no)$/i.test(x)) >= 0.9) return null;
  return null;
};

// Returns [{ col, header, field ('' = ignore, 'notes+' = add to notes), confidence 0-1, reason }]
export function autoMap(headers, sampleRows) {
  const used = new Set(); const out = [];
  headers.forEach((h, col) => {
    const n = norm(h); let best = null;
    for (const [field, list] of Object.entries(SYN)) {
      if (list.includes(n)) { best = [field, 0.95, 'header match']; break; }
    }
    if (!best) for (const [field, list] of Object.entries(SYN)) if (list.some((s) => s.length > 3 && (n.startsWith(`${s} `) || n.endsWith(` ${s}`) || n.includes(s)))) { best = [field, 0.6, 'partial header match']; break; }
    const pat = patternGuess(sampleRows.map((r) => r[col]));
    if (pat && (!best || (best[0] !== pat[0] && best[1] < 0.9))) best = best && best[0] === pat[0] ? [pat[0], 0.97, 'header and data agree'] : [pat[0], best ? 0.5 : pat[1], best ? 'header and data disagree' : 'looks like this from the data'];
    else if (pat && best && best[0] === pat[0]) best = [best[0], 0.99, 'header and data agree'];
    out.push({ col, header: h, field: best ? best[0] : '', confidence: best ? best[1] : 0, reason: best ? best[2] : 'no match' });
  });
  // One column per field: keep the highest confidence, mark the rest ambiguous.
  for (const m of [...out].sort((a, b) => b.confidence - a.confidence)) {
    if (!m.field) continue;
    if (used.has(m.field)) { m.reason = `another column is already ${m.field}`; m.confidence = Math.min(m.confidence, 0.4); m.ambiguous = true; } else used.add(m.field);
  }
  return out.map((m) => ({ ...m, confirmed: m.confidence >= 0.8 && !m.ambiguous }));
}
export const needsReview = (m) => !m.confirmed;

const yes = (v) => /^(y|yes|true|1|x)$/i.test(String(v || '').trim());
const TYPES = ['Buyer', 'Seller', 'Tenant', 'Landlord', 'Agent', 'Vendor', 'Other'];

// Build contact records from rows + mapping. Returns { records, errors }.
export function buildRecords(rows, mapping) {
  const records = []; const errors = [];
  rows.forEach((r, i) => {
    const get = (f) => mapping.filter((m) => m.field === f).map((m) => String(r[m.col] || '').trim()).find(Boolean) || '';
    let firstName = get('firstName'); let lastName = get('lastName');
    if (!firstName && !lastName && get('fullName')) { const p = get('fullName').split(/\s+/); if (/,/.test(get('fullName'))) { const [l, f] = get('fullName').split(',').map((x) => x.trim()); firstName = f || ''; lastName = l || ''; } else { firstName = p.shift() || ''; lastName = p.join(' '); } }
    const rawPhone = get('phone'); const phone = digits10(rawPhone);
    const email = get('email').toLowerCase();
    const problems = [];
    if (!firstName && !lastName) problems.push('no name');
    if (rawPhone && !phone) problems.push(`phone "${rawPhone}" is not 10 digits`);
    if (email && !EMAIL.test(email)) problems.push(`email "${email}" looks wrong`);
    const extraNotes = mapping.filter((m) => m.field === 'notes+' && String(r[m.col] || '').trim()).map((m) => `${m.header}: ${String(r[m.col]).trim()}`);
    const notes = [get('notes'), ...extraNotes].filter(Boolean).join('\n');
    const types = get('contactTypes').split(/[,;/]/).map((t) => t.trim()).filter(Boolean).map((t) => TYPES.find((x) => x.toLowerCase() === t.toLowerCase()) || null).filter(Boolean);
    const street = get('street'); const city = get('city'); const state = get('state').toUpperCase(); const zip = get('zip');
    records.push({
      row: i + 2, problems,
      data: {
        firstName, lastName, name: [firstName, lastName].filter(Boolean).join(' '),
        phone: phone || '', email: email && EMAIL.test(email) ? email : '',
        street, city, state, zip, address: [street, city, [state, zip].filter(Boolean).join(' ')].filter(Boolean).join(', '),
        contactTypes: [...new Set(types)], source: get('source'), tags: get('tags').split(/[,;]/).map((t) => t.trim()).filter(Boolean),
        notes, consentText: yes(get('consentText')), consentEmail: yes(get('consentEmail')),
      },
      assignedEmail: get('assignedEmail').toLowerCase(),
      dnc: yes(get('dnc')),
    });
  });
  return { records, errors };
}

// Duplicate by phone (last 10) or email against existing contacts and earlier rows in the file.
export function findDuplicate(rec, existing, earlier) {
  // Same person = same phone (incl. second phones and numbers removed for Do Not Call) or same email.
  const d = rec.data; const hit = (c) => !c.mergedInto && isStrong(matchReasons(d, c));
  return { existing: existing.find(hit) || null, inFile: earlier.some((e) => hit(e.data)) };
}

// Patch for updating an existing contact: fill only empty fields, union types/tags, consent only turns on.
export function updatePatch(c, d) {
  const p = {};
  ['firstName', 'lastName', 'phone', 'email', 'street', 'city', 'state', 'zip', 'address', 'source'].forEach((k) => { if (d[k] && !String(c[k] || '').trim()) p[k] = d[k]; });
  const types = [...new Set([...(c.contactTypes || []), ...(d.contactTypes || [])])]; if (types.length !== (c.contactTypes || []).length) p.contactTypes = types;
  const tags = [...new Set([...(c.tags || []), ...(d.tags || [])])]; if (tags.length !== (c.tags || []).length) p.tags = tags;
  if (d.consentText && !c.consentText) p.consentText = true;
  if (d.consentEmail && !c.consentEmail) p.consentEmail = true;
  if (d.notes && !String(c.notes || '').includes(d.notes)) p.notes = [c.notes, d.notes].filter(Boolean).join('\n');
  // Existing contact on Do Not Call: never add a phone back. New DNC = Y on an existing contact: remove its phones.
  if (c.dnc) delete p.phone;
  return p;
}
